import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SimulationParams } from './types';

export interface ParameterEditorProps {
  params: SimulationParams;
  onChange: (params: SimulationParams) => void;
  disabled?: boolean;
}

export function ParameterEditor({ params, onChange, disabled = false }: ParameterEditorProps) {
  const updateParam = <K extends keyof SimulationParams>(
    key: K,
    value: SimulationParams[K]
  ) => {
    onChange({ ...params, [key]: value });
  };

  return (
    <div className="space-y-3">
      {/* Manning's n */}
      <div className="grid grid-cols-2 items-center gap-2">
        <Label className="text-xs">Manning's n</Label>
        <Input
          type="number"
          value={params.manning_n}
          onChange={(e) => updateParam('manning_n', parseFloat(e.target.value) || 0)}
          step={0.005}
          min={0.01}
          max={0.1}
          disabled={disabled}
          className="h-8 text-xs"
        />
      </div>

      {/* Infiltration Factor */}
      <div className="grid grid-cols-2 items-center gap-2">
        <Label className="text-xs">Infiltration Factor</Label>
        <Input
          type="number"
          value={params.infiltration_factor}
          onChange={(e) => updateParam('infiltration_factor', parseFloat(e.target.value) || 0)}
          step={0.05}
          min={0}
          max={0.9}
          disabled={disabled}
          className="h-8 text-xs"
        />
      </div>

      {/* Duration */}
      <div className="grid grid-cols-2 items-center gap-2">
        <Label className="text-xs">Duration (min)</Label>
        <Input
          type="number"
          value={params.duration_minutes}
          onChange={(e) => updateParam('duration_minutes', parseFloat(e.target.value) || 0)}
          step={10}
          min={10}
          max={480}
          disabled={disabled}
          className="h-8 text-xs"
        />
      </div>

      {/* Time Step */}
      <div className="grid grid-cols-2 items-center gap-2">
        <Label className="text-xs">Time Step (sec)</Label>
        <Input
          type="number"
          value={params.dt_seconds}
          onChange={(e) => updateParam('dt_seconds', parseFloat(e.target.value) || 0)}
          step={10}
          min={1}
          max={300}
          disabled={disabled}
          className="h-8 text-xs"
        />
      </div>

      {/* Rainfall Rate */}
      <div className="grid grid-cols-2 items-center gap-2">
        <Label className="text-xs">Rainfall Rate</Label>
        <Input
          type="number"
          value={params.rainfall_rate}
          onChange={(e) => updateParam('rainfall_rate', parseFloat(e.target.value) || 0)}
          step={5}
          min={0}
          max={200}
          disabled={disabled}
          className="h-8 text-xs"
        />
      </div>

      {/* Rainfall Units */}
      <div className="grid grid-cols-2 items-center gap-2">
        <Label className="text-xs">Rainfall Units</Label>
        <Select
          value={params.rainfall_units}
          onValueChange={(value: 'mm/hr' | 'mm/min') => updateParam('rainfall_units', value)}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mm/hr">mm/hr</SelectItem>
            <SelectItem value="mm/min">mm/min</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Min Pond Size */}
      <div className="grid grid-cols-2 items-center gap-2">
        <Label className="text-xs">Min Pond Size (px)</Label>
        <Input
          type="number"
          value={params.min_pond_size_pixels}
          onChange={(e) => updateParam('min_pond_size_pixels', parseFloat(e.target.value) || 0)}
          step={1}
          min={1}
          max={100}
          disabled={disabled}
          className="h-8 text-xs"
        />
      </div>

      {/* Min Pond Depth */}
      <div className="grid grid-cols-2 items-center gap-2">
        <Label className="text-xs">Min Pond Depth (m)</Label>
        <Input
          type="number"
          value={params.min_pond_depth_meters}
          onChange={(e) => updateParam('min_pond_depth_meters', parseFloat(e.target.value) || 0)}
          step={0.005}
          min={0.001}
          max={0.5}
          disabled={disabled}
          className="h-8 text-xs"
        />
      </div>
    </div>
  );
}
